import { Typography } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import type { DynamicInvoiceIdType } from '@weft/api-definition';
import { VyIcon } from '@weft/icons';
import { DEFAULT_CURRENCY, INVOICE_REVIEW_STATUSES, REVISIONED_LINE_ITEM_TYPES } from '@weft/types';
import { isNotEmptyArray,
  outersect,
  validateNotEmptyString,
  validateNotNullOrUndefined,
  withDefault } from '@weft/utils';
import { useParamsDynamicInvoiceIdOrDefault } from 'apps/app/src/hooks/use-dynamic-invoice-id';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { NavLink } from 'react-router-dom';
import { VyPageLayout } from '../../../components/new-design/layout/VyPageLayout';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { useDynamicInvoice } from '../../../hooks/requests/use-dynamic-invoice';
import { useLineItemsSplitter } from '../../../hooks/use-line-items-splitter';
import { DynamicInvoiceContextProvider } from './dynamic-invoice-context';
import { AddRevisionedLineItemButton } from './dynamic-invoice-view-components/AddRevisionedLineItemButton';
import { DynamicInvoiceHeader } from './dynamic-invoice-view-components/dynamic-invoice-header/DynamicInvoiceHeader';
import { FixedLineItemsSection } from './dynamic-invoice-view-components/dynamic-invoice-sections/FixedLineItemsSection';
import { PromotionLineItemsSection } from './dynamic-invoice-view-components/dynamic-invoice-sections/PromotionLineItemsSection';
import { RevisionedLineItemsDialogsSection } from './dynamic-invoice-view-components/dynamic-invoice-sections/RevisionedLineItemsDialogsSection';
import { RevisionedLineItemsSection } from './dynamic-invoice-view-components/dynamic-invoice-sections/RevisionedLineItemsSection';
import { UsageLineItemsSection } from './dynamic-invoice-view-components/dynamic-invoice-sections/UsageLineItemsSection';
import { DynamicInvoiceSummary } from './dynamic-invoice-view-components/DynamicInvoiceSummary';
import type { RevisionedLineItemDialogForms } from './dynamic-invoice-view-components/revisioned-line-item-dialog-forms/revisioned-line-item-dialog-forms-types/revisioned-line-item-dialog-type';
import type { DialogsState } from './dynamic-invoice-view-components/revisioned-line-item-dialog-types/dialogs-state';
import type { RevisionedLineItemDialogType } from './dynamic-invoice-view-components/revisioned-line-item-dialog-types/revisioned-line-item-dialog-type';
import { RevisionedLineItemDialogs } from './dynamic-invoice-view-components/revisioned-line-item-dialog-types/revisioned-line-item-dialog-type';

type DynamicInvoiceViewPageProps = {
  id?: string;
  idType: DynamicInvoiceIdType;
  noRedirectOnApprove?: boolean;
  doNotUsePageLayout?: boolean; // TODO: after all customer page would move to new layout, then this prop should be removed
};

const DynamicInvoiceViewPage: React.FC<DynamicInvoiceViewPageProps> = ({
  id,
  idType,
  doNotUsePageLayout = false,
  noRedirectOnApprove = false,
}) => {
  const dynamicInvoiceId = useParamsDynamicInvoiceIdOrDefault(id);

  validateNotEmptyString(dynamicInvoiceId, 'dynamicInvoiceId');

  const { data: dynamicInvoiceDataResponse } = useDynamicInvoice(dynamicInvoiceId, idType);
  validateNotNullOrUndefined(dynamicInvoiceDataResponse, 'dynamicInvoiceDataResponse');

  const { dynamicInvoiceData } = dynamicInvoiceDataResponse;
  const { measurementChartData } = dynamicInvoiceData;

  const customerSettings = dynamicInvoiceData.customer.settings;
  const dynamicInvoiceQueryKey = ['dynamicInvoice', dynamicInvoiceId];

  const { lineItems, revisionedLineItems = [], revenueBreakdown } = dynamicInvoiceData.invoice;
  const { fixedLineItems, usageLineItems, commitmentLineItems, promotionLineItems, volumeTiersLineItems } = useLineItemsSplitter(lineItems);
  const nonVolumeUsageLineItems = outersect(usageLineItems, volumeTiersLineItems);

  const fixedRevisionedLineItems = revisionedLineItems.filter((lineItem) => lineItem.type === REVISIONED_LINE_ITEM_TYPES.Fixed);
  const fixedPromotionRevisionedLineItems = revisionedLineItems.filter((lineItem) => lineItem.type === REVISIONED_LINE_ITEM_TYPES.FixedPromotion);
  const percentagePromotionRevisionedLineItems = revisionedLineItems.filter((lineItem) => lineItem.type === REVISIONED_LINE_ITEM_TYPES.PercentagePromotion);

  const [dialogsState, setDialogsState] = useState<DialogsState>({
    [RevisionedLineItemDialogs.Fixed]: {
      open: false,
    },
    [RevisionedLineItemDialogs.Promotion]: {
      open: false,
    },
  });

  const handleRevisionedLineItemClick = (revisionedLineItemDialogType: RevisionedLineItemDialogType, lineItemId?: string, defaultValues?: RevisionedLineItemDialogForms[typeof revisionedLineItemDialogType]) => {
    return () => {
      setDialogsState((previousState) => ({
        ...previousState,
        [revisionedLineItemDialogType]: {
          ...previousState[revisionedLineItemDialogType],
          lineItemId,
          defaultValues,
          open: true,
        },
      }));
    };
  };

  const handleAddRevisionedItemSelect = (revisionedLineItemDialogType: RevisionedLineItemDialogType) => {
    setDialogsState((previousState) => ({
      ...previousState,
      [revisionedLineItemDialogType]: {
        ...previousState[revisionedLineItemDialogType],
        lineItemId: undefined,
        defaultValues: undefined,
        open: true,
      },
    }));
  };

  const isEditDisabled = dynamicInvoiceData.invoice.reviewStatus === INVOICE_REVIEW_STATUSES.Approved;

  const Content = (
    <DynamicInvoiceContextProvider dynamicInvoiceDataResponse={dynamicInvoiceDataResponse} >
      <Stack
        sx={{ maxWidth: '1200px', paddingTop: '40px' }}
        margin="auto"
      >
        <DynamicInvoiceHeader
          dynamicInvoiceDataResponse={dynamicInvoiceDataResponse}
          currency={withDefault(customerSettings?.currency, DEFAULT_CURRENCY)}
          queryKeyToInvalidate={dynamicInvoiceQueryKey}
          noRedirectOnApprove={noRedirectOnApprove}
        />
        <Stack
          spacing={5}
          paddingTop={2}
        >
          {(isNotEmptyArray(fixedRevisionedLineItems) || isNotEmptyArray(fixedPromotionRevisionedLineItems) || isNotEmptyArray(percentagePromotionRevisionedLineItems)) && (
            <RevisionedLineItemsSection
              queryKeyToInvalidate={dynamicInvoiceQueryKey}
              invoiceId={dynamicInvoiceData.invoice.id}
              revisionedLineItems={revisionedLineItems}
              promotionLineItems={promotionLineItems}
              onRevisionedLineItemClick={handleRevisionedLineItemClick}
            />
          )}

          {isNotEmptyArray(fixedLineItems) && (
            <FixedLineItemsSection
              invoiceId={dynamicInvoiceData.invoice.id}
              customerId={dynamicInvoiceData.customer.id}
              fixedLineItems={fixedLineItems}
              measurementChartData={measurementChartData}
            />
          )}

          {(isNotEmptyArray(nonVolumeUsageLineItems) || isNotEmptyArray(commitmentLineItems)) && (
            <UsageLineItemsSection
              invoiceId={dynamicInvoiceData.invoice.id}
              title="Usage fees"
              customerId={dynamicInvoiceData.customer.id}
              commitmentLineItems={commitmentLineItems}
              usageLineItems={nonVolumeUsageLineItems}
              measurementChartData={measurementChartData}
            />
          )}

          {(isNotEmptyArray(volumeTiersLineItems)) && (
            <UsageLineItemsSection
              invoiceId={dynamicInvoiceData.invoice.id}
              title="Usage fees - Volume"
              customerId={dynamicInvoiceData.customer.id}
              commitmentLineItems={[]}
              usageLineItems={volumeTiersLineItems}
              measurementChartData={measurementChartData}
            />
          )}

          {(isNotEmptyArray(promotionLineItems)) && (
            <PromotionLineItemsSection
              promotionLineItems={promotionLineItems}
            />
          )}

          {!isEditDisabled && (
            <AddRevisionedLineItemButton
              cta={(
                <Stack
                  direction="row"
                  spacing={1}
                >
                  <VyIcon
                    icon="PlusOutlined"
                    width={24}
                    height={24}
                  />
                  <Typography variant="body1">
                    Add item
                  </Typography>
                </Stack>
              )}
              sx={theme => ({
                color: theme.palette.text.secondary,
              })}
            >
              <MenuItem onClick={() => handleAddRevisionedItemSelect(RevisionedLineItemDialogs.Fixed)}>
                <Typography variant="body1">
                  Fixed fee
                </Typography>
              </MenuItem>

              <MenuItem
                disabled={isNotEmptyArray(revisionedLineItems)}
                onClick={() => handleAddRevisionedItemSelect(RevisionedLineItemDialogs.Promotion)}
              >
                <Typography variant="body1">
                  Discount
                </Typography>
              </MenuItem>
            </AddRevisionedLineItemButton>
          )}

          <RevisionedLineItemsDialogsSection
            invoiceId={dynamicInvoiceData.invoice.id}
            dialogsState={dialogsState}
            setDialogsState={setDialogsState}
            revisionedLineItems={revisionedLineItems}
            promotionLineItems={promotionLineItems}
            queryKeyToInvalidate={dynamicInvoiceQueryKey}
          />

          <DynamicInvoiceSummary
            revenueBreakdown={revenueBreakdown}
            currency={withDefault(customerSettings?.currency, DEFAULT_CURRENCY)}
          />
        </Stack> 
      </Stack>
    </DynamicInvoiceContextProvider>
  );

  return (
    <>
      <Helmet>
        <title>{`Invoice ${dynamicInvoiceData.invoice.name} ${dynamicInvoiceData.customer.name}`}</title>
      </Helmet>

      {doNotUsePageLayout ? Content : (
        <VyPageLayout
          title={(
            <NavLink to={`/customers/${dynamicInvoiceData.customer.id}`}>
              <Typography variant="h5">{dynamicInvoiceData.customer.name}</Typography>
            </NavLink>
          )}
          path={['Home', <NavLink to="/invoices">Invoice</NavLink>, ' Dynamic Invoice']}
          sx={{
            paddingTop: 0,
          }}
        >
          {Content}
        </VyPageLayout>
      )}
    </>
  );
};

const DynamicInvoiceViewPageWithSuspenseBoundary = withSuspenseBoundary(DynamicInvoiceViewPage);

export { DynamicInvoiceViewPageWithSuspenseBoundary as DynamicInvoiceViewPage };